import React, {useEffect, useMemo, useState} from 'react';

import {useAction, useSelectorWithRefresh} from '@/store/kysely_module';
import {useCurrentWorkspaceId} from '@/store/modules/workspaces_module';
import {Comment} from './comment';
import {CommentForm} from './comment_form';
import {Button, Group} from '@mantine/core';

type Props = {
    parent_id: string;
    parent_type: 'File' | 'Project';
    clickedTimestamp: (timestamp: string) => void;
    onInitialFocus?: () => string;
    refreshComments: () => void;
    newTimestamp?: {timestamp: string} | null;
};

export const useCommentActions = () => {
    return {
        addCommentToFile: useAction('addCommentToFile'),
        deleteComment: useAction('deleteComment'),
        editComment: useAction('editComment'),
    };
};

export const CommentSection = (props: Props) => {
    const workspaceId = useCurrentWorkspaceId();
    const [showCommentForm, setShowCommentForm] = useState(false);
    const [shouldAutoFocus, setShouldAutoFocus] = useState(false);

    const [originalComments, refreshComments] = useSelectorWithRefresh(
        'getCommentsAndRepliesForParent',
        {parent_id: props.parent_id, parent_type: props.parent_type},
        [],
        {workspace_id: workspaceId}
    );

    const [workspaceUsers] = useSelectorWithRefresh('getUsersForWorkspace', {
        workspace_id: workspaceId,
        excludeSelf: false
    }, [], {workspace_id: workspaceId});

    const comments = useMemo(() => [...originalComments].reverse(), [originalComments]);

    const actions = useCommentActions();

    useEffect(() => {
        if (props.newTimestamp) {
            setShowCommentForm(true);
            setShouldAutoFocus(true);
        }
    }, [props.newTimestamp]);

    const submitComment = async (text: string) => {
        await actions.addCommentToFile({
            comment_text: text,
            file_id: props.parent_id,
        }, {workspace_id: workspaceId});

        refreshComments();
        setShouldAutoFocus(false);
    };

    const replyToComment = async (text: string, parentId: string) => {
        await actions.addCommentToFile({
            file_id: props.parent_id,
            comment_text: text,
            reply_parent_id: parentId,
        }, {workspace_id: workspaceId});

        refreshComments();
    };

    const deleteComment = async (commentId: string) => {
        await actions.deleteComment({comment_id: commentId}, {workspace_id: workspaceId});
        refreshComments();
    };

    const editComment = async (commentId: string, text: string) => {
        await actions.editComment({comment_id: commentId, text}, {workspace_id: workspaceId});
        refreshComments();
    };

    const hasComments = comments.length > 0;
    const shouldShowForm = !hasComments || showCommentForm;

    return (
        <>
            <Group gap="md" align="center" mb="1.5rem">
                <h3 style={{margin: 0}}>Comments</h3>
                {!shouldShowForm && (
                    <Button
                        onClick={() => {
                            setShowCommentForm(true);
                            setShouldAutoFocus(true);
                        }}
                        variant="light"
                        size="xs"
                    >
                        Add Comment
                    </Button>
                )}
            </Group>
            {shouldShowForm && (
                <div style={{marginBottom: '1rem'}}>
                    <CommentForm
                        onSubmit={submitComment}
                        onInitialFocus={props.onInitialFocus}
                        newTimestamp={props.newTimestamp}
                        onCancel={hasComments ? () => {
                            setShowCommentForm(false);
                            setShouldAutoFocus(false);
                        } : undefined}
                        autoFocus={shouldAutoFocus}
                    />
                </div>
            )}
            <div
                className='comment-list'
            >
                {comments?.map((c, i) => (
                    <Comment
                        key={c.id}
                        showUser={true}
                        comment={c}
                        clickedTimestamp={props.clickedTimestamp}
                        deleteComment={deleteComment}
                        editComment={editComment}
                        replyToComment={replyToComment}
                        onInitialFocus={props.onInitialFocus}
                        workspaceUsers={workspaceUsers}
                    />
                ))}
            </div>
        </>
    );
};

export const parseTimestamps = (message: string): string[] => {
    return message.match(/([0-9]+:)?[0-9]+:[0-5][0-9]/g) || [];
}
